import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HeaderText from '../../Common/HeaderText';
import HeaderTab from './HeaderTab';
import ApplicationHistoryModal from './ApplicationHistoryModal';
import useJobSeekingCertificateDownload from '../../../../utils/useJobSeekingCertificateDownload';
import ApplicationsListForm from './ApplicationsListForm';
import { Toast, Modal, useAuth, useIsMobile, useT } from '@repo/common';
import {
  deleteApplies,
  getMyApplies,
  getMyAppliesCount,
} from '../../../../api/jobboard/apply.api';
import {
  deleteMultipleBookmarks,
  getMyBookmarks,
} from '../../../../api/jobboard/jobboard.api';

const HIGHLIGHT_THRESHOLD = 2;

const STATUS_LABEL_MAP = {
  'All statuses': 'ALL',
  Submitted: 'SUBMITTED',
  Accepted: 'ACCEPTED',
  'Need prep': 'NEEDPREP', // 백엔드 ApplyStatus enum 값 (NEED_PREP 아님)
};

const VALID_APPLY_TABS = ['submitted', 'accepted', 'needPrep', 'saved', 'applied'];

const Applications = () => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  // /mypage?tab=My Apply&applyTab=saved 로 들어오면 해당 탭으로 연다
  const [selectedTab, setSelectedTab] = useState(() => {
    const requested = new URLSearchParams(location.search).get('applyTab');
    if (requested && VALID_APPLY_TABS.includes(requested)) return requested;
    return isMobile ? 'applied' : 'submitted';
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedApply, setSelectedApply] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showDeletedSuccess, setShowDeletedSuccess] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState('All time');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [certificateToast, setCertificateToast] = useState(null);

  const [counts, setCounts] = useState({
    submitted: 0,
    accepted: 0,
    needPrep: 0,
    saved: 0,
  });

  const { user } = useAuth();
  const { download: downloadCertificate, isDownloading: isCertificateDownloading } =
    useJobSeekingCertificateDownload(user);

  const handleDownloadCertificate = async (period = 'ALL') => {
    const result = await downloadCertificate(period);
    setCertificateToast(null);
    setTimeout(
      () =>
        setCertificateToast(
          result.ok
            ? {
                type: 'success',
                title: t(
                  'Certificate downloaded.',
                  '구직활동 증명서를 내려받았습니다.',
                ),
              }
            : {
                type: 'error',
                title: t(
                  "Couldn't issue the certificate. Please try again.",
                  '증명서를 발급하지 못했습니다. 다시 시도해 주세요.',
                ),
              },
        ),
      10,
    );
    setTimeout(() => setCertificateToast(null), 3000);
  };

  const fetchCounts = useCallback(async () => {
    try {
      const res = await getMyAppliesCount();
      setCounts({
        submitted: res?.submitted ?? 0,
        accepted: res?.accepted ?? 0,
        needPrep: res?.needPrep ?? 0,
        saved: res?.saved ?? 0,
        applied:
          (res?.submitted ?? 0) + (res?.accepted ?? 0) + (res?.needPrep ?? 0),
      });
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setCurrentPage(1);
  }, [selectedTab]);

  const fetchHistory = useCallback(async () => {
    const PERIOD_MAP = {
      'All time': 'ALL',
      'Last 1 month': '1M',
      'Last 3 month': '3M',
      'Last 4 month': '4M',
    };

    try {
      const period = PERIOD_MAP[filterPeriod];
      const page = currentPage - 1;
      const size = 7;

      if (selectedTab === 'saved') {
        const data = await getMyBookmarks(period, page, size);
        setDisplayData(Array.isArray(data.content) ? data.content : []);
        setTotalPages(data.totalPages || 1);
      } else {
        const statusMap = {
          submitted: 'SUBMITTED',
          accepted: 'ACCEPTED',
          needPrep: 'NEEDPREP',
          applied: STATUS_LABEL_MAP[statusFilter] ?? 'ALL',
        };
        const data = await getMyApplies(
          period,
          statusMap[selectedTab],
          page,
          size,
        );
        setDisplayData(Array.isArray(data.content) ? data.content : []);
        setTotalPages(data.totalPages || 1);
      }
    } catch (e) {
      console.error(e);
    }
  }, [filterPeriod, currentPage, selectedTab, statusFilter]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    setSelectedIds([]);
    setCurrentPage(1);
  }, [selectedTab, statusFilter]);

  const handleRowClick = (apply) => {
    if (selectedTab === 'saved') {
      navigate(`/jobs/${apply.jobPostId}`, {
        state: { source: 'mypage_saved' },
      });
    }
    setSelectedApply(apply);
    setIsModalOpen(true);
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const getId = (item) =>
    selectedTab === 'saved' ? item.jobPostId : item.jobApplyId;

  const handleSelectAll = (checked) => {
    if (checked) {
      const mappedIds = displayData.map((a) => getId(a));
      setSelectedIds(mappedIds);
    } else {
      setSelectedIds([]);
    }
  };

  const getDeleteModalContent = () => {
    const count = selectedIds.length;
    const isMany = count >= HIGHLIGHT_THRESHOLD;

    const countText = isMany ? (
      <span className='text-text-accent'>{count}</span>
    ) : (
      count
    );

    if (selectedTab === 'needPrep') {
      return {
        title:
          count === 1 ? (
            'Remove this application?'
          ) : (
            <>Remove {countText} applications?</>
          ),
        subText:
          selectedIds.length === 1
            ? `This application will be removed from your list.\nThis action cannot be undone.`
            : `${selectedIds.length} applications will be removed from your list.\nThis action cannot be undone.`,
      };
    }
    if (selectedTab === 'saved') {
      return {
        title:
          count === 1 ? (
            'Remove this saved job?'
          ) : (
            <>Remove {countText} saved jobs?</>
          ),

        subText:
          selectedIds.length === 1
            ? `This saved job will be removed from your list.\nThis action cannot be undone.`
            : `${selectedIds.length} saved jobs will be removed from your list.\nThis action cannot be undone.`,
      };
    }
    return {
      title:
        count === 1 ? (
          'Withdraw this application?'
        ) : (
          <>Withdraw {countText} applications?</>
        ),
      subText:
        selectedIds.length === 1
          ? `This application will be withdrawn and removed from your list.\nThis action cannot be undone.`
          : `${selectedIds.length} applications will be withdrawn and removed from your list.\nThis action cannot be undone.`,
    };
  };

  const getDeleteModalButtonLabel = () => {
    const isRemoveTarget =
      selectedTab === 'needPrep' || selectedTab === 'saved';

    return isRemoveTarget ? 'Remove' : 'Withdraw';
  };

  const getDeleteSuccessToastLabel = () => {
    if (selectedTab === 'needPrep') {
      return 'Application Deleted';
    }

    if (selectedTab === 'saved') {
      return 'Removed from Saved Jobs';
    }

    return 'Application withdrawn';
  };

  const { title, subText } = getDeleteModalContent();

  const handleDelete = async () => {
    try {
      if (selectedTab === 'saved') {
        await deleteMultipleBookmarks(selectedIds);
      } else {
        await deleteApplies(selectedIds);
      }
      await fetchCounts();
      await fetchHistory();
      setIsDeleteModalOpen(false);
      setShowDeletedSuccess(false);
      setTimeout(() => setShowDeletedSuccess(true), 10);
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleWithdrawFromModal = (applyId) => {
    setSelectedIds([applyId]);
    setIsDeleteModalOpen(true);
  };

  return (
    <div className='relative flex flex-col w-full items-start tabletH:pb-[60px]'>
      <HeaderText label='Applications' />
      <HeaderTab
        counts={counts}
        selectedTab={selectedTab}
        onChangeTab={setSelectedTab}
        $isMobile={isMobile}
      />
      <ApplicationsListForm
        applies={displayData}
        selectedTab={selectedTab}
        onRowClick={handleRowClick}
        selectedIds={selectedIds}
        onCheckboxChange={handleCheckboxChange}
        onSelectAll={handleSelectAll}
        onDelete={() => setIsDeleteModalOpen(true)}
        onDeleteCancel={() => setIsDeleteModalOpen(false)}
        onConfirmDelete={handleDelete}
        isAllSelected={
          displayData.length > 0 && selectedIds.length === displayData.length
        }
        onFilterChange={setFilterPeriod}
        filterPeriod={filterPeriod}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        current={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        $isMobile={isMobile}
        onDownloadCertificate={handleDownloadCertificate}
        isCertificateDownloading={isCertificateDownloading}
      />

      {certificateToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <Toast
            type={certificateToast.type}
            state='solid'
            title={certificateToast.title}
            hierarchy='important'
            direction='up'
            customWidth='auto'
          />
        </div>
      )}

      {selectedTab !== 'saved' && (
        <ApplicationHistoryModal
          job={selectedApply}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
          }}
          onDelete={() => handleWithdrawFromModal(selectedApply?.jobApplyId)}
          $isMobile={isMobile}
        />
      )}

      {showDeletedSuccess && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div
            className={`w-full ${
              selectedTab === 'saved'
                ? 'max-w-[290px] tabletH:w-[290px]'
                : 'max-w-[270px] tabletH:w-[270px]'
            }`}
          >
            <Toast
              type='success'
              state='white'
              title={getDeleteSuccessToastLabel()}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        type='warning'
        title={title}
        subText={subText}
        leftButtonLabel='Cancel'
        rightButtonLabel={getDeleteModalButtonLabel()}
        leftHideIcon
        rightHideIcon
        onClickLeftBtn={() => setIsDeleteModalOpen(false)}
        onClickRightBtn={handleDelete}
        bgclass='bg-grayscale-gray00 tabletH:bg-grayscale-gray01'
      />
    </div>
  );
};

export default Applications;
